import Connector from '../objects/Connector';
import Frame from '../objects/Frame';
import Path from '../objects/Path';
import Shape from '../objects/Shape';
import Stamp from '../objects/Stamp';
import Wireframe from '../objects/Wireframe';
import Comment from '../objects/Comment';
import { fabric } from 'fabric';
import { randomId } from '@aha-app/mvc';
import version from '../version';
import Table from '../objects/Table';
import SmartObject from '../objects/SmartObject';
import EmbeddedBookmark from '../objects/EmbeddedBookmark';
import DataStoreGlobal from '../collaboration/DataStoreGlobal';
import { CustomShape } from '../models/CustomShape';

import widgetFromJSON from '../widgets/widgetFromJSON';
import {
  hasDataStoreRecordBehaviors,
  hasFrameBehaviors,
  isConnector,
  isSimpleGroup,
} from '../objects/guards';
import type WhiteboardController from '../controllers/WhiteboardController';
import type Canvas from '../objects/Canvas';
import { type RecordId } from '../types';
import Device from '../objects/Device';
import { type IDataStoreRecordBehaviors } from './DataStoreRecordBehaviors';
import SvgStamp from '../objects/SvgStamp';

export interface DocData<T = any> {
  objects: Record<string, T>;
}
export type TableKey = 'objects' | 'global' | 'widgets' | 'customShapes';

export interface CopiedWhiteboardContent<T = any> extends DocData<T> {
  version: string;
  whiteboardId: RecordId;
  customShapes?: DocData['objects'];
}

export interface PastedWhiteboardContent<T = any> extends DocData<T> {
  version?: string;
  whiteboardId?: RecordId;
  customShapes?: DocData['objects'];
}

class Serialization {
  // Clone a document objects structure, replacing all ids with new ones, and
  // removing references to ids that are not in the document. This does not
  // clone the non-object data stores. In the future other data stores should
  // be cloned as well as appropriate.
  // idMap will be populated with a mapping of new object ids to old object ids.
  static cloneDocument(docData: DocData, idMap = {}): DocData {
    // First make a clone.
    docData = JSON.parse(JSON.stringify(docData)) as DocData;

    // Build an index of all objects.
    const newObjects = {};
    for (const obj of Object.values(docData.objects)) {
      const oldId = obj.id;
      // Reset object ID.
      obj.id = randomId();
      newObjects[obj.id] = obj;
      if (idMap) {
        idMap[obj.id] = oldId;
      }
    }

    // Special case handling for embedded object IDs.
    for (const obj of Object.values(docData.objects)) {
      // A group object groups together other objects by listing their ids in
      // a sub objects array. Convert those ids to the new ones.
      if (obj.objects) {
        obj.objects = obj.objects.map(
          oldObjectId => docData.objects[oldObjectId]?.id
        );
      }

      // Connectors are listed under the attachments key and point to the
      // objects connected at both ends of the connector.
      if (obj.attachments) {
        const cloneAttachment = old => {
          // The copied data might include references to objects that were not
          // copied - clear those out.
          if (old && docData.objects[old.objectId]) {
            return {
              objectId: docData.objects[old.objectId].id,
              attachmentPoint: old.attachmentPoint,
            };
          } else {
            return null;
          }
        };

        obj.attachments = [
          cloneAttachment(obj.attachments[0]),
          cloneAttachment(obj.attachments[1]),
        ];
      }

      // Update label on a connector with the new connector id. It's not
      // possible to copy a label without its connector.
      if (obj.connectorId && docData.objects[obj.connectorId]) {
        obj.connectorId = docData.objects[obj.connectorId].id;
      }

      // Update the container id if this object has one
      if (obj.container && docData.objects[obj.container]) {
        obj.container = docData.objects[obj.container].id;
      }
    }

    return {
      objects: newObjects,
    };
  }

  static async cloneDocumentWithAttachments(
    docDataArg: PastedWhiteboardContent,
    controller: WhiteboardController
  ) {
    const { whiteboardId } = docDataArg;
    const docData = Serialization.cloneDocument(docDataArg);

    // We only want to duplicate attachments if we are copying from a
    // different whiteboard.
    if (!whiteboardId || controller.whiteboardId === whiteboardId) {
      return docData;
    }

    const attachmentIds = {};

    for (const obj of Object.values(docData.objects)) {
      // Handle images pasted into new whiteboards - the attachment will
      // need to be duplicated and the new ID stored.
      if (obj.attachmentId) {
        attachmentIds[obj.id] = obj.attachmentId;
      }
    }

    // We process these in a batch to minimise API calls
    if (Object.keys(attachmentIds).length > 0) {
      const attachmentIdMapping = await controller.actionDuplicateAttachments(
        attachmentIds,
        whiteboardId
      );

      Object.keys(attachmentIdMapping).forEach(id => {
        if (docData.objects[id]) {
          docData.objects[id].attachmentId = attachmentIdMapping[id];
        }
      });
    }

    return docData;
  }

  // Serialize a set of objects into a document. Can be used to serialize an
  // entire canvas, or a selection (for copy/paste).
  static collectionToJSON(
    objects: fabric.Object[],
    whiteboardId: RecordId
  ): CopiedWhiteboardContent {
    const allObjects = {};

    // Recursively collect all objects contained in groups.
    const collectObjects = (objects: fabric.Object[]) => {
      for (const obj of objects.filter(hasDataStoreRecordBehaviors)) {
        allObjects[obj.uniqueId()] = obj.toJSON();
        if (isConnector(obj)) {
          collectObjects(obj.labels);
        }
        if (isSimpleGroup(obj)) {
          collectObjects(obj.getObjects());
        }
        if (hasFrameBehaviors(obj) && obj.frameBehaviorsEnabled) {
          collectObjects(obj.children);
        }
      }
    };
    collectObjects(objects);

    return {
      version,
      whiteboardId,
      objects: allObjects,
    };
  }

  // Add objects described in a JSON document to the canvas.
  static restoreDocument(
    controller: WhiteboardController,
    docData: DocData,
    currentObjectIds?: string[]
  ) {
    // Validate that docData is internally consistent, and clean any problems.
    docData = this.validateDoc(controller, docData, currentObjectIds);

    /* We need to discard outgoing ops if we're using a client that is going to send these
     * ops to Faye (ie a non-loopback client) and the user isn't allowed to make updates.
     * Faye will reject those ops as unauthorized which will break the whiteboard.
     * The Loopback client doesn't connect to Faye and therefore doesn't need to
     * discard outgoing ops. If it does discard ops then this can break shared whiteboards
     * when the op is a create or delete (because we throw an error if we try to discard those).
     */
    if (!controller.props.allowUpdate && !controller.client.isLoopbackClient) {
      controller.client.discardOutgoingOps = true;
    }
    try {
      // Disable layout calculations during document restoration to prevent
      // frames from being sized before their children are loaded
      controller.aroundBulkAddingCode(() => {
        // Loop over our datastores.
        for (const tableKey in controller.dataStores) {
          const objectsToProcess = { ...docData[tableKey] };
          if (objectsToProcess.length === 0) continue;

          if (tableKey === 'objects') {
            // Make sure that groups are processed in depth first order.
            const objectsToProcess = { ...docData.objects };
            const objectsForCanvas: any[] = [];
            const recursivelyAddObjects = (objectId, depth) => {
              if (depth > 50) return; // Prevent and infinite loop from circular group references.

              const objectData = objectsToProcess[objectId];
              if (!objectData) return;

              if (objectData.type === 'group') {
                // Recurse for the group members first.
                objectData.objects.forEach(memberId =>
                  recursivelyAddObjects(memberId, depth + 1)
                );
              }

              objectsForCanvas.push(this.restoreObject(controller, objectData));
              delete objectsToProcess[objectId];
            };

            while (true) {
              const objectId = Object.keys(objectsToProcess)[0];
              if (!objectId) break;
              recursivelyAddObjects(objectId, 0);
            }

            // Add all of the objects to the canvas.
            controller.canvas.addObjects(
              objectsForCanvas.filter(obj => !obj.simpleGroup)
            );
          } else {
            for (const objectId in objectsToProcess) {
              this.restoreRecord(
                controller,
                tableKey as TableKey,
                objectId,
                objectsToProcess[objectId]
              );
            }
          }
        }
      });
    } finally {
      /* HACK: Enabling outgoing ops needs to be put in a timeout because we have at
      least one place where we defer a op-emitting action with setTimeout.
      https://github.com/aha-app/aha-app/blob/AW-276-1-prevent-all-writes-and-re-enable-migration/packages/whiteboard/src/fabric/SimpleGroup.tsx#L118

      If we don't also use setTimeout here then we would re-enable outgoing op before that
      other timeout executes, causing an op to be emitted that should have been discarded.

      Ideally we wouldn't use setTimeout really anywhere.
      */
      setTimeout(() => (controller.client.discardOutgoingOps = false));
    }
  }

  static restoreObject(
    controller: WhiteboardController,
    objectData: any
  ): fabric.Object {
    const initialData = {
      ...controller.defaultsManager.defaultFor(
        objectData.type,
        objectData.shapeName || objectData.wireframeName
      ),
      ...objectData,
    };
    if (objectData.type === 'group') {
      initialData.objects = objectData.objects
        .map(childObjectId => {
          const obj = controller.dataStoreObjects.findRecordById(childObjectId);
          return obj;
        })
        .filter(obj => obj);

      for (const obj of initialData.objects) {
        // Make sure the object is not on the canvas. This matters when
        // undoing ungrouping.
        controller.canvas.remove(obj);
      }

      controller.canvas.collectionNeedsSort = true;
    }
    const object = Serialization.fromJSON('objects', {
      controller,
      ...initialData,
    }) as fabric.Object & IDataStoreRecordBehaviors;

    controller.dataStoreObjects.addRecord(object);

    return object;
  }

  static restoreRecord(
    controller: WhiteboardController,
    tableKey: TableKey,
    objectId: string,
    objectData: any
  ) {
    const initialData = { ...objectData };
    const dataStore = controller.dataStoreForKey(tableKey);

    if (!dataStore) throw new Error(`Unknown data store '${tableKey}'`);

    const object = Serialization.fromJSON(tableKey, {
      controller,
      ...initialData,
    });

    dataStore.addRecord(object);

    return object;
  }

  // Given a DocData, finds all the records that were restored from it
  // and do not belong in a group. This is useful for passing to
  // canvas.makeActiveSelection
  static getUngroupedObjects(canvas: Canvas, docData: DocData) {
    const allIds: RecordId[] = [];
    let groupMemberIds: RecordId[] = [];
    Object.values(docData.objects).forEach(obj => {
      allIds.push(obj.id);
      if (obj.type === 'group') {
        groupMemberIds.push(obj.objects);
      }
    });
    groupMemberIds = groupMemberIds.flat(Infinity).filter(objId => objId);
    const selectableIds = allIds.filter(
      objId => !groupMemberIds.includes(objId)
    );
    const selectedObjects = selectableIds.map(objectId =>
      canvas.controller.dataStoreObjects.findRecordById(objectId)
    );

    return selectedObjects as fabric.Object[];
  }

  // Reconstruct an object from JSON. I am replacing the built-in
  // `fromObject` methods here to have more control over the process.
  static fromJSON(tableKey: TableKey, data: any) {
    switch (tableKey) {
      case 'objects':
        return this.objectTypeToInstance(data.type, data);

      case 'global':
        return DataStoreGlobal.fromJSON(data);

      case 'widgets':
        return widgetFromJSON(data);

      case 'customShapes':
        return new CustomShape(data);

      default: {
        const _exhaustivenessCheck: never = tableKey;
        return _exhaustivenessCheck;
      }
    }
  }

  static objectTypeToInstance(type: string, data: any) {
    switch (type) {
      case 'shape':
        return new Shape(data);
      case 'connector':
        return new Connector(data.points, data);
      case 'group': {
        const { objects = [], ...groupData } = data;
        return new fabric.Group(objects, groupData);
      }
      case 'path':
        return new Path(data.path, data);
      case 'table':
        return new Table(data);
      case 'embeddedBookmark':
        return new EmbeddedBookmark(data);
      case 'frame':
        return new Frame(data);
      case 'stamp':
        return new Stamp(data);
      case 'svgStamp':
        return new SvgStamp(data);
      case 'comment':
        return new Comment(data);
      case 'wireframe':
        return new Wireframe(data);
      case 'device':
        return new Device(data);
      case 'smartObject':
        return new SmartObject(data);
      default:
        throw new Error(`Unknown object class type '${type}'`);
    }
  }

  static transformWhiteboardContent = <T = any>(
    clipboardContents: PastedWhiteboardContent<T> | null | undefined,
    controller: WhiteboardController,
    retainLayoutProperties: boolean = false
  ): PastedWhiteboardContent<T> | undefined => {
    if (!clipboardContents) {
      return;
    }

    const canvasComments = this.extractNestedComments(
      controller.canvas.getObjects()
    );

    const groups: any[] = [];
    const deletedComments: unknown[] = [];
    for (const obj of Object.values(clipboardContents?.objects) as any) {
      if (obj.type === 'group') groups.push(obj);

      if (obj.type !== 'comment') {
        obj.authorId = controller.state.userId;
      }

      if (obj.type === 'comment') {
        obj.commentable = null;
      }

      // Remove dependency line info from connectors
      if (obj.type === 'connector' && obj.dependencyLineInfo) {
        obj.dependencyLineInfo = null;
      }

      // If a comment exists on the canvas with the same annotationId,
      // then we're copying an existing comment.
      // If the comment does not exist in the annotationIdCache, then
      // we're copying a comment from a different whiteboard.
      // In either of these scenarios, we don't want to paste the comment.
      if (
        obj.type === 'comment' &&
        (canvasComments.find(o => o.annotationId === obj.annotationId) ||
          !controller.annotationIdExists(obj.annotationId))
      ) {
        deletedComments.push(obj.id);
        delete clipboardContents.objects[obj.id];
      }
    }

    // If we're pasting a group, and the group contains comments that
    // were deleted, then we need to remove those comments from the
    // group.
    if (groups.length && deletedComments.length) {
      for (const commentId of deletedComments) {
        const group = groups.find(g => g.objects.includes(commentId));
        if (group) {
          group.objects = group.objects.filter(id => id !== commentId);
          // If the group only contained removed comments, remove it.
          if (group.objects.length === 0) {
            delete clipboardContents.objects[group.id];
          }
        }
      }
    }

    if (!retainLayoutProperties) {
      this.cleanupContainerProperties(clipboardContents);
    }

    return clipboardContents;
  };

  private static extractNestedComments(
    objects: fabric.Object[]
  ): fabric.Object[] {
    const comments: fabric.Object[] = [];

    for (const obj of objects) {
      if (isSimpleGroup(obj)) {
        comments.push(...this.extractNestedComments(obj.getObjects()));
      } else if (obj.type === 'comment') {
        comments.push(obj);
      }
    }

    return comments;
  }

  // Validate that a document is internally consistent. Returns a cleaned
  // version of the document. If currentObjectIds is provided, then objects
  // can reference those objects outside the document (making it not
  // internally consistent).
  // Clean up container properties when objects are pasted
  // Only null out container and layoutPosition if the container doesn't exist in the copied contents
  static cleanupContainerProperties<T = any>(
    clipboardContents: PastedWhiteboardContent<T>
  ) {
    if (!clipboardContents.objects) {
      return;
    }

    // Get all object IDs in the clipboard contents
    const existingObjectIds = new Set(Object.keys(clipboardContents.objects));

    // Check each object and clean up container properties only if needed
    for (const obj of Object.values(clipboardContents.objects) as any[]) {
      // Only null out container and layoutPosition if the container doesn't exist in the copied contents
      if (obj.container && !existingObjectIds.has(obj.container)) {
        obj.container = null;
        obj.layoutPosition = null;
      }
    }
  }

  static validateDoc(
    controller: WhiteboardController,
    docData: DocData,
    currentObjectIds?: string[]
  ) {
    const newDoc = {};
    const allIds = Object.keys(docData.objects).concat(currentObjectIds ?? []);

    const missingObjectIds: string[] = [];
    for (const objectId in docData.objects) {
      const object = docData.objects[objectId];

      // Check the object for basic consistency.
      if (!object.type) {
        console.log(object);
        throw new Error("Object in document doesn't have a type");
      }

      if (object.type === 'group') {
        const groupMissingIds = object.objects.filter(
          id => !allIds.includes(id)
        );
        if (groupMissingIds.length > 0) {
          console.error('Group members missing from objects', groupMissingIds);
          missingObjectIds.push(groupMissingIds);
        }
        object.objects = object.objects.filter(id => allIds.includes(id));
      }
    }
    if (missingObjectIds.length > 0) {
      console.error(
        `Group members missing from objects: ${JSON.stringify(
          missingObjectIds
        )}`
      );
    }

    for (const tableKey in controller.dataStores) {
      if (docData.hasOwnProperty(tableKey))
        newDoc[tableKey] = docData[tableKey]; // Not a deep copy!
    }

    return newDoc as DocData;
  }
}

export type ObjectType = ReturnType<
  (typeof Serialization)['objectTypeToInstance']
>;

export default Serialization;
